import { createContext, useCallback, useContext, useMemo } from 'react';

import type { FetchArgs } from 'agora-rtc-react';
import { useParams } from 'react-router-dom';

import issueJoinTicketOrFallBackToParticipantId, { JoinTicket } from '@/app/queries/MeetingQuery/joinTicket';
import { cameraUidsOf, screenShareUidsOf } from '@/app/queries/MeetingQuery/participantUids';

export interface IParticipant {
  is_host: boolean;
  user_id: number;
  screen_share_id: number;
  join_uids: number[];
  screen_share_join_uids: number[];
  user_name: string;
}

export interface MeetingData {
  token: string;
  screen_share_token: string;
  meeting_data: {
    channel_name: string;
    house_address: string;
    appointment_start: string | null;
    is_host: boolean;
    screen_share_id: number;
    user_id: number;
    user_name: string;
    meeting_urn: string;
    review_urls: string[];
  },
  participants: IParticipant[];
}

export const MeetingDataContext = createContext<MeetingData>(null!);

export function useMeetingData() {
  return useContext(MeetingDataContext);
}

export const JoinTicketContext = createContext<JoinTicket>(null!);

export function useJoinData(appid: string, onTicket: (ticket: JoinTicket) => void): FetchArgs {
  const { hash } = useParams<{ hash: string }>();
  const data = useMeetingData();

  return useCallback(async() => {
    const ticket = await issueJoinTicketOrFallBackToParticipantId(hash!, data);
    onTicket(ticket);

    return {
      appid,
      token: ticket.token,
      channel: data.meeting_data.channel_name,
      uid: ticket.uid,
    };
  }, [appid, data, hash, onTicket]);
}

export function useShareData(appid: string) {
  const data = useMeetingData();
  const ticket = useContext(JoinTicketContext);

  return useMemo(() => ({
    appid,
    token: ticket.screen_share_token,
    channel: data.meeting_data.channel_name,
    uid: ticket.screen_share_uid,
  }), [appid, data, ticket]);
}

export function useUserData() {
  const data = useMeetingData();

  return useMemo(() => ({
    uid: data.meeting_data.user_id,
    name: data.meeting_data.user_name,
    address: data.meeting_data.house_address,
    isClient: !data.meeting_data.is_host,
    isHost: data.meeting_data.is_host,
  }), [data]);
}

/**
 * Retrieves the meeting participant data for a given user ID.
 * Current user and screensharing users are also considered as participants
 * @param userId
 */
export function useParticipantData(userId: number) {
  const { participants } = useMeetingData();

  return useMemo(() => {
    const cameraOwner = participants.find(user => cameraUidsOf(user).includes(userId));
    const screenShareOwner = participants.find(user => screenShareUidsOf(user).includes(userId));
    const requestedParticipant = cameraOwner ?? screenShareOwner;

    if (requestedParticipant) {
      return {
        uid: requestedParticipant.user_id,
        name: requestedParticipant.user_name,
        isClient: !requestedParticipant.is_host,
        isHost: requestedParticipant.is_host,
        isScreenShare: !cameraOwner,
      };
    }
  }, [userId, participants]);
}
